/**
 * TerminologyPanel - click-to-insert standard docket phrases for the SOP Details field.
 */
import { useState } from "react";
import axios from "axios";
import { BookOpenText, ChevronDown, ChevronUp, Plus, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TerminologyPanel = ({ pin, kioskKey, onInsert }) => {
  const [open, setOpen] = useState(false);
  const [sections, setSections] = useState(null);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && sections === null && !loading) {
      setLoading(true);
      try {
        const auth = kioskKey ? `key=${encodeURIComponent(kioskKey)}` : `pin=${encodeURIComponent(pin)}`;
        const res = await axios.get(`${API}/sop/terminology?${auth}`);
        setSections(res.data.sections || []);
      } catch (err) {
        setSections([]);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={toggle}
        data-testid="sop-phrases-toggle"
        className="flex items-center gap-1.5 text-xs font-bold text-emerald-700 hover:text-emerald-900 transition-colors"
      >
        <BookOpenText className="w-4 h-4" />
        Standard Phrases — click to insert
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>

      {open && (
        <div className="mt-2 border-2 border-emerald-100 bg-emerald-50/40 rounded-xl p-3" data-testid="sop-phrases-panel">
          {loading && (
            <div className="flex items-center gap-2 text-gray-500 text-sm py-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading phrases...
            </div>
          )}
          {sections && sections.length > 0 && (
            <>
              <div className="flex gap-1.5 overflow-x-auto pb-2 mb-2 -mx-1 px-1">
                {sections.map((s, i) => (
                  <button
                    key={s.title}
                    type="button"
                    onClick={() => setActive(i)}
                    data-testid={`sop-phrase-section-${i}`}
                    className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                      active === i ? 'bg-emerald-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-emerald-400'
                    }`}
                  >
                    {s.title}
                  </button>
                ))}
              </div>
              <ul className="max-h-44 overflow-y-auto space-y-1">
                {(sections[active]?.phrases || []).map((p, i) => (
                  <li key={p}>
                    <button
                      type="button"
                      onClick={() => onInsert(p)}
                      data-testid={`sop-phrase-${i}`}
                      className="w-full flex items-start gap-2 text-left px-2.5 py-1.5 rounded-lg text-xs text-gray-700 bg-white hover:bg-emerald-100 hover:text-emerald-900 border border-gray-100 transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-emerald-600" />
                      <span>{p}</span>
                    </button>
                  </li>
                ))}
              </ul>
              <p className="text-[10px] text-gray-400 mt-2">
                Fill in the [BRACKETED] details after inserting. Everything here goes into the PrintLogic job description.
              </p>
            </>
          )}
          {sections && sections.length === 0 && !loading && (
            <p className="text-sm text-gray-500 py-1">No phrases available.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default TerminologyPanel;
